import { useState, useRef, useEffect, useCallback } from "react"
import {
  SpinnerGap,
  Check,
  ArrowCounterClockwise,
  ArrowClockwise,
  X,
} from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import {
  initSam,
  encodeSamImage,
  decodeSamMask,
  applySamMask,
  resetSamState,
  isSamInitialized,
} from "../../lib/sam"
import type { SamPoint } from "../../lib/sam"

interface SamOverlayProps {
  imageFile: File
  onAccept: (processedFile: File) => void
  onCancel: () => void
}

type LoadingPhase = "model" | "encoding" | "decoding" | null

export function SamOverlay({ imageFile, onAccept, onCancel }: SamOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [points, setPoints] = useState<SamPoint[]>([])
  const [maskData, setMaskData] = useState<ImageData | null>(null)
  const [loadingPhase, setLoadingPhase] = useState<LoadingPhase>("model")
  const [isAccepting, setIsAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isEncoded, setIsEncoded] = useState(false)
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null)
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })
  const [scale, setScale] = useState(1)

  // Load model and encode image on mount
  useEffect(() => {
    let cancelled = false

    async function setup() {
      try {
        if (!isSamInitialized()) {
          setLoadingPhase("model")
          await initSam()
        }

        if (cancelled) return

        setLoadingPhase("encoding")
        await encodeSamImage(imageFile)

        if (cancelled) return
        setIsEncoded(true)
        setLoadingPhase(null)
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to initialize SAM"
          )
          setLoadingPhase(null)
        }
      }
    }

    setup()

    return () => {
      cancelled = true
      resetSamState()
    }
  }, [imageFile])

  // Load image element for canvas rendering
  useEffect(() => {
    const url = URL.createObjectURL(imageFile)
    const img = new Image()
    img.onload = () => {
      setImageElement(img)
    }
    img.src = url

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [imageFile])

  // Calculate canvas size to fit viewport
  useEffect(() => {
    if (!imageElement) return

    const updateSize = () => {
      const maxW = window.innerWidth - 48 // 24px padding each side
      const maxH = window.innerHeight - 140 // header + bottom padding
      const imgW = imageElement.naturalWidth
      const imgH = imageElement.naturalHeight
      const s = Math.min(maxW / imgW, maxH / imgH, 1)

      setScale(s)
      setCanvasSize({
        width: Math.round(imgW * s),
        height: Math.round(imgH * s),
      })
    }

    updateSize()
    window.addEventListener("resize", updateSize)
    return () => window.removeEventListener("resize", updateSize)
  }, [imageElement])

  // Render canvas: image + mask + points
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !imageElement || canvasSize.width === 0) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    canvas.width = canvasSize.width
    canvas.height = canvasSize.height

    // Draw original image
    ctx.drawImage(imageElement, 0, 0, canvasSize.width, canvasSize.height)

    // Draw mask overlay
    if (maskData) {
      const maskCanvas = document.createElement("canvas")
      maskCanvas.width = maskData.width
      maskCanvas.height = maskData.height
      const maskCtx = maskCanvas.getContext("2d")
      if (maskCtx) {
        maskCtx.putImageData(maskData, 0, 0)
        // Draw semi-transparent blue overlay where mask is set
        const tempCanvas = document.createElement("canvas")
        tempCanvas.width = maskData.width
        tempCanvas.height = maskData.height
        const tempCtx = tempCanvas.getContext("2d")
        if (tempCtx) {
          const blueOverlay = tempCtx.createImageData(
            maskData.width,
            maskData.height
          )
          const src = maskData.data
          const dst = blueOverlay.data
          for (let i = 0; i < src.length; i += 4) {
            if (src[i + 3] > 0) {
              // Mask pixel is set
              dst[i] = 59 // R (blue tint)
              dst[i + 1] = 130 // G
              dst[i + 2] = 246 // B
              dst[i + 3] = 100 // A
            }
          }
          tempCtx.putImageData(blueOverlay, 0, 0)
          ctx.drawImage(tempCanvas, 0, 0, canvasSize.width, canvasSize.height)
        }
      }
    }

    // Draw point markers
    for (const point of points) {
      const x = point.x * scale
      const y = point.y * scale
      const radius = 6

      ctx.beginPath()
      ctx.arc(x, y, radius, 0, Math.PI * 2)
      ctx.fillStyle = point.label === 1 ? "#22c55e" : "#ef4444"
      ctx.fill()
      ctx.strokeStyle = "#ffffff"
      ctx.lineWidth = 2
      ctx.stroke()

      // Draw +/- symbol
      ctx.fillStyle = "#ffffff"
      ctx.font = "bold 10px sans-serif"
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText(point.label === 1 ? "+" : "-", x, y)
    }
  }, [imageElement, canvasSize, maskData, points, scale])

  // Decode mask when points change
  useEffect(() => {
    if (points.length === 0 || !isEncoded) return

    let cancelled = false

    async function decode() {
      setLoadingPhase("decoding")
      try {
        const result = await decodeSamMask(points)
        if (!cancelled) {
          setMaskData(result.mask)
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to decode mask"
          )
        }
      } finally {
        if (!cancelled) {
          setLoadingPhase(null)
        }
      }
    }

    decode()
    return () => {
      cancelled = true
    }
  }, [points, isEncoded])

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (loadingPhase === "model" || loadingPhase === "encoding") return

      const canvas = canvasRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const canvasX = e.clientX - rect.left
      const canvasY = e.clientY - rect.top

      // Convert to original image coordinates
      const originalX = canvasX / scale
      const originalY = canvasY / scale

      setPoints((prev) => [...prev, { x: originalX, y: originalY, label: 1 }])
    },
    [scale, loadingPhase]
  )

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault()
      if (loadingPhase === "model" || loadingPhase === "encoding") return

      const canvas = canvasRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const canvasX = e.clientX - rect.left
      const canvasY = e.clientY - rect.top

      const originalX = canvasX / scale
      const originalY = canvasY / scale

      setPoints((prev) => [...prev, { x: originalX, y: originalY, label: 0 }])
    },
    [scale, loadingPhase]
  )

  const handleUndo = useCallback(() => {
    setPoints((prev) => {
      const next = prev.slice(0, -1)
      if (next.length === 0) {
        setMaskData(null)
      }
      return next
    })
  }, [])

  const handleReset = useCallback(() => {
    setPoints([])
    setMaskData(null)
  }, [])

  const handleAccept = useCallback(async () => {
    if (!maskData) return

    setIsAccepting(true)
    try {
      const processedFile = await applySamMask(imageFile, maskData)
      onAccept(processedFile)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to apply mask"
      )
      setIsAccepting(false)
    }
  }, [maskData, imageFile, onAccept])

  const isReady = loadingPhase === null && !error
  const isDisabled = loadingPhase !== null || isAccepting

  const loadingMessage =
    loadingPhase === "model"
      ? "Loading SAM model..."
      : loadingPhase === "encoding"
        ? "Encoding image..."
        : loadingPhase === "decoding"
          ? "Generating mask..."
          : null

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-3">
        <h2 className="text-lg font-semibold text-foreground">
          Interactive Segmentation
          {loadingMessage && (
            <span className="ml-3 inline-flex items-center gap-2 text-sm font-normal text-muted-foreground">
              <SpinnerGap className="size-4 animate-spin" />
              {loadingMessage}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleUndo}
            disabled={isDisabled || points.length === 0}
          >
            <ArrowCounterClockwise className="mr-1 size-4" />
            Undo
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={isDisabled || points.length === 0}
          >
            <ArrowClockwise className="mr-1 size-4" />
            Reset
          </Button>
          <Button
            size="sm"
            onClick={handleAccept}
            disabled={isDisabled || !maskData}
          >
            {isAccepting ? (
              <>
                <SpinnerGap className="mr-1 size-4 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <Check className="mr-1 size-4" weight="bold" />
                Accept
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isAccepting}
          >
            <X className="mr-1 size-4" />
            Cancel
          </Button>
        </div>
      </div>

      {/* Main canvas area */}
      <div className="flex flex-1 items-center justify-center p-6">
        {error ? (
          <div className="text-center">
            <p className="text-lg text-destructive">{error}</p>
            <Button variant="outline" className="mt-4" onClick={onCancel}>
              Go Back
            </Button>
          </div>
        ) : loadingPhase === "model" || loadingPhase === "encoding" ? (
          <div className="flex flex-col items-center gap-4">
            <SpinnerGap className="size-12 animate-spin text-primary" />
            <p className="text-lg text-muted-foreground">{loadingMessage}</p>
          </div>
        ) : (
          <div className="relative">
            <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              className="cursor-crosshair rounded-lg shadow-lg"
              onClick={handleCanvasClick}
              onContextMenu={handleContextMenu}
              style={{
                width: canvasSize.width,
                height: canvasSize.height,
              }}
            />
            {isReady && points.length === 0 && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <p className="rounded-lg bg-background/80 px-4 py-2 text-sm text-muted-foreground">
                  Left-click to select, right-click to deselect
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
